import { useEffect, useState } from 'react';
import { Archive, AlertCircle, TrendingDown, RefreshCw, Loader2, Search, X } from 'lucide-react';

/**
 * InventoryView — Layer 2: Full inventory stock table.
 *
 * Shows all products with stock per location.
 * Low-stock and out-of-stock items are highlighted.
 * Toggle to show all products or only low-stock ones.
 */
export function InventoryView({ dashboardData }) {
  const { inventory, inventorySummary, inventoryLoading, inventoryError, fetchInventory } = dashboardData;

  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch inventory on mount
  useEffect(() => {
    fetchInventory(lowStockOnly);
  }, [lowStockOnly, fetchInventory]);

  // Client-side filter by product title or SKU
  const filteredProducts = inventory.filter(p => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      p.product_title?.toLowerCase().includes(term) ||
      p.sku?.toLowerCase().includes(term)
    );
  });

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-4">

        {/* Header row */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-montserrat font-extrabold text-xl text-gray-800">
              Inventario
            </h2>
            {inventorySummary && (
              <p className="font-montserrat text-sm text-gray-500 mt-0.5">
                {inventorySummary.total_products} productos ·{' '}
                <span className={inventorySummary.low_stock_count > 0 ? 'text-amber-600 font-semibold' : ''}>
                  {inventorySummary.low_stock_count} con stock bajo
                </span>
                {inventorySummary.out_of_stock_count > 0 && (
                  <span className="text-red-600 font-semibold">
                    {' '}· {inventorySummary.out_of_stock_count} sin stock
                  </span>
                )}
              </p>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Low stock toggle */}
            <button
              onClick={() => setLowStockOnly(prev => !prev)}
              className={`flex items-center gap-2 text-sm font-montserrat font-semibold px-3 py-2 rounded-lg border transition-colors ${
                lowStockOnly
                  ? 'bg-amber-50 border-amber-300 text-amber-700'
                  : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
              }`}
            >
              <TrendingDown size={15} />
              Stock bajo
            </button>

            {/* Refresh */}
            <button
              onClick={() => fetchInventory(lowStockOnly)}
              disabled={inventoryLoading}
              className="flex items-center gap-2 text-sm font-montserrat font-semibold text-gray-500 hover:text-brand-primary transition-colors disabled:opacity-50 px-3 py-2 rounded-lg border border-gray-200 hover:border-gray-300"
            >
              <RefreshCw size={15} className={inventoryLoading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {/* Search bar */}
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Buscar producto o SKU..."
            className="w-full pl-9 pr-8 py-2.5 text-sm font-montserrat bg-white border border-gray-200 rounded-xl focus:outline-none focus:border-brand-primary transition-colors"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* Error */}
        {inventoryError && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm font-montserrat text-red-600">
            {inventoryError}
          </div>
        )}

        {/* Table */}
        {inventoryLoading ? (
          <div className="flex items-center justify-center py-24 gap-3">
            <Loader2 size={24} className="animate-spin text-brand-primary" />
            <span className="font-montserrat text-gray-400 text-sm">Cargando inventario...</span>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 gap-3">
            <Archive size={40} className="text-gray-200" />
            <p className="font-montserrat text-gray-400 text-sm">
              {lowStockOnly ? 'Sin productos con stock bajo' : 'Sin productos en inventario'}
            </p>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <Th>Producto</Th>
                  <Th>SKU</Th>
                  <Th>Precio</Th>
                  <Th>Stock Total</Th>
                  <Th>Por Ubicación</Th>
                  <Th>Estado</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredProducts.map(product => {
                  const isOut  = product.total_stock === 0;
                  const isLow  = product.is_low_stock && !isOut;

                  return (
                    <tr
                      key={product.product_id}
                      className={
                        isOut ? 'bg-red-50/50' :
                        isLow ? 'bg-amber-50/50' :
                        'hover:bg-gray-50/60'
                      }
                    >
                      {/* Product */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {product.primary_image ? (
                            <img
                              src={product.primary_image}
                              alt={product.product_title}
                              className="w-10 h-10 rounded-lg object-cover bg-gray-100 shrink-0"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                              <Archive size={16} className="text-gray-300" />
                            </div>
                          )}
                          <span className="font-montserrat font-semibold text-sm text-gray-800">
                            {product.product_title}
                          </span>
                        </div>
                      </td>

                      {/* SKU */}
                      <td className="px-4 py-3">
                        <span className="font-montserrat text-xs text-gray-400 font-mono bg-gray-100 px-2 py-0.5 rounded">
                          {product.sku}
                        </span>
                      </td>

                      {/* Price */}
                      <td className="px-4 py-3">
                        <span className="font-montserrat text-sm text-gray-600">
                          S/ {product.price?.toFixed(2)}
                        </span>
                      </td>

                      {/* Total stock */}
                      <td className="px-4 py-3">
                        <span className={`font-montserrat font-extrabold text-lg ${
                          isOut ? 'text-red-600' : isLow ? 'text-amber-600' : 'text-gray-800'
                        }`}>
                          {product.total_stock}
                        </span>
                        <span className="font-montserrat text-xs text-gray-400 ml-1">uds</span>
                      </td>

                      {/* Per-location breakdown */}
                      <td className="px-4 py-3">
                        <div className="space-y-1">
                          {product.locations.map(loc => (
                            <div key={loc.location_id} className="flex items-center gap-2">
                              <span className="text-xs font-montserrat text-gray-500 truncate max-w-[100px]">
                                {loc.location_name}
                              </span>
                              <span className={`text-xs font-montserrat font-bold ${
                                loc.is_low_stock ? 'text-amber-600' : 'text-gray-700'
                              }`}>
                                {loc.quantity}
                              </span>
                            </div>
                          ))}
                        </div>
                      </td>

                      {/* Stock status badge */}
                      <td className="px-4 py-3">
                        {isOut ? (
                          <span className="flex items-center gap-1 text-xs font-montserrat font-bold text-red-600 bg-red-100 px-2.5 py-1 rounded-full w-fit">
                            <AlertCircle size={11} />
                            Sin stock
                          </span>
                        ) : isLow ? (
                          <span className="flex items-center gap-1 text-xs font-montserrat font-bold text-amber-600 bg-amber-100 px-2.5 py-1 rounded-full w-fit">
                            <TrendingDown size={11} />
                            Stock bajo
                          </span>
                        ) : (
                          <span className="text-xs font-montserrat text-green-600 bg-green-50 px-2.5 py-1 rounded-full w-fit block">
                            ✓ Saludable
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Th({ children }) {
  return (
    <th className="px-4 py-3 text-left text-xs font-montserrat font-bold text-gray-500 uppercase tracking-wide whitespace-nowrap">
      {children}
    </th>
  );
}